import type { ReactNode } from 'react'
import React from 'react'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useTranslation } from 'react-i18next'
import { Title } from '@fs/zion-head'
import { usePreference } from '@fs/zion-preferences'
import { getNameInPreferredScript } from '@fs/zion-person-service'
import { usePersonPageParams } from '../person-page/personPageRouteUtil'
import ResearchHelpProvider from '../person-page/contexts/ResearchHelpContext'
import FocusFamilyMembersProvider from '../person-page/contexts/FocusFamilyMembersContext'
import useFocusPerson from '../data-service/useFocusPerson'
import FocusPIDProvider from './FocusPIDContext'
import PersonNotesProvider from './NotesProvider'
import ExpandedSectionsProvider from './ExpandedSectionsProvider'
import RecordHintLevelPrefProvider from './RecordHintLevelPrefProvider'
import useSwitchTree from '../hooks/useSwitchTree'

type Props = {
  children: ReactNode
  loadingComponent: ReactNode
}

// exported just for test
export function PersonAppProvidersCore({ children, loadingComponent }: Props): JSX.Element {
  const [t] = useTranslation()
  const { focusPerson, isLoading, notFound } = useFocusPerson()
  const showNameInRomanScriptPreference = usePreference<boolean>('tree.showNamesInRomanScript')
  const { fullName } = getNameInPreferredScript(focusPerson, Boolean(showNameInRomanScriptPreference))
  const { selectTree, currentTreeId, isResearcherGroup } = useGroupsContext()
  const { subPage } = usePersonPageParams('full')
  const unknownLabel = t('tree-person.unknown.label')

  const personIsInAResearcherGroup = focusPerson?.researcherTree
  const personTreeId = focusPerson?.treeId

  useSwitchTree({ focusPerson, selectTree, currentTreeId, isResearcherGroup })

  if (isLoading) return <>{loadingComponent}</>

  return (
    <>
      {(focusPerson || notFound) && (
        <Title>
          {t('tree-person.details.browser.title', {
            personName: fullName ?? unknownLabel,
            lifespan: focusPerson?.lifespan ?? unknownLabel,
            subPage,
          })}
        </Title>
      )}

      <PersonNotesProvider>
        <RecordHintLevelPrefProvider loadingComponent={loadingComponent}>
          <ResearchHelpProvider isResearcherGroup={personIsInAResearcherGroup} treeId={personTreeId}>
            {children}
          </ResearchHelpProvider>
        </RecordHintLevelPrefProvider>
      </PersonNotesProvider>
    </>
  )
}

export default function PersonAppProviders({ children, loadingComponent }: Props): JSX.Element {
  return (
    <FocusPIDProvider>
      <ExpandedSectionsProvider loadingComponent={loadingComponent}>
        {/* Hoisted above PersonAppProvidersCore's `if (isLoading)` gate so the family-members fetch
            (keyed only on pid + treeId) runs in parallel with the person-card read instead of after it. */}
        <FocusFamilyMembersProvider>
          <PersonAppProvidersCore loadingComponent={loadingComponent}>{children}</PersonAppProvidersCore>
        </FocusFamilyMembersProvider>
      </ExpandedSectionsProvider>
    </FocusPIDProvider>
  )
}
